import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useVerifyEmailMutation } from "@/features/api/authApi";

const VerifyEmail = () => {
  const { token } = useParams(); // token from URL
  const navigate = useNavigate();
  const { toast } = useToast();

  const [verifyEmail, { isLoading }] = useVerifyEmailMutation();
  const [status, setStatus] = useState(null); // null | "success" | "error"

  useEffect(() => {
    if (!token) {
      setStatus("error");
      toast({
        title: "Error",
        description: "Verification token is missing.",
        variant: "destructive",
      });
      return;
    }

    const verify = async () => {
      try {
        const response = await verifyEmail({ token }).unwrap();
        setStatus("success");
        toast({
          title: "Success",
          description: response?.message || "Email verified successfully!",
        });
      } catch (err) {
        console.error("Email verification failed:", err);
        setStatus("error");
        toast({
          title: "Error",
          description: err?.data?.message || "Failed to verify email.",
          variant: "destructive",
        });
      }
    };

    verify();
  }, [token, verifyEmail, toast]);

  return (
    <div className="flex min-h-[65vh] items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardContent className="p-6 flex flex-col items-center text-center">
          {isLoading ? (
            <>
              <Loader2 className="animate-spin h-10 w-10 mb-4" />
              <p className="text-lg font-medium">Verifying your email...</p>
            </>
          ) : status === "success" ? (
            <>
              <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
              <h2 className="text-2xl font-bold mb-2">Email Verified!</h2>
              <p className="mb-6">Your email has been successfully verified. You can now login.</p>
              <Button onClick={() => navigate("/login")}>Go to Login</Button>
            </>
          ) : (
            <>
              <XCircle className="h-16 w-16 text-red-500 mb-4" />
              <h2 className="text-2xl font-bold mb-2">Verification Failed</h2>
              <p className="mb-6">Invalid or expired verification link. Please try again.</p>
              <Button onClick={() => navigate("/")}>Go to Home</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
